import React, { useCallback, useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import PageHeader from "@/components/app/PageHeader";
import StatusBadge, { STATUS_LABEL } from "@/components/app/StatusBadge";
import { toast } from "sonner";
import { ArrowRight, AlertTriangle, Clock, Timer, Map as MapIcon, LayoutGrid } from "lucide-react";
import TrackingMap from "@/pages/tracking/TrackingMap";

const AVG_SPEED_MPH = 40;
const LOAD_UNLOAD_MIN = 15;

function haversineMi(lat1, lon1, lat2, lon2) {
  const toRad = (d) => (d * Math.PI) / 180;
  const R = 3958.7613;
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a = Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(a));
}
function travelEstimate(pickup, drop) {
  if (!pickup?.lat || !pickup?.lon || !drop?.lat || !drop?.lon) return null;
  const d = haversineMi(pickup.lat, pickup.lon, drop.lat, drop.lon);
  const oneWay = (d / AVG_SPEED_MPH) * 60;
  const rt = oneWay * 2 + LOAD_UNLOAD_MIN * 2;
  return { distance: d, oneWay, roundTrip: rt };
}
function fmtMin(m) {
  if (m == null) return "—";
  const absM = Math.abs(m);
  if (absM < 60) return `${Math.round(absM)}m`;
  const h = Math.floor(absM / 60);
  const min = Math.round(absM % 60);
  return `${h}h ${min}m`;
}

/** Timestamp (ms) that a load first entered `in_transit`. Null if never. */
function inTransitStartMs(load) {
  const events = load.custody_events || [];
  const ev = events.find((e) => e.event_type === "in_transit");
  if (!ev?.at) return null;
  const t = Date.parse(ev.at);
  return Number.isFinite(t) ? t : null;
}

/** Live "now" tick every 30s to keep countdowns fresh without heavy refresh. */
function useNowTick(ms = 30000) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), ms);
    return () => clearInterval(id);
  }, [ms]);
  return now;
}

const COLUMNS = [
  { key: "scheduled", label: "Scheduled" },
  { key: "picked_up", label: "Picked Up" },
  { key: "in_transit", label: "In Transit" },
  { key: "delivered", label: "Delivered" },
];

export default function Tracking() {
  const [loads, setLoads] = useState([]);
  const [sites, setSites] = useState([]);
  const [trucks, setTrucks] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [view, setView] = useState(() => localStorage.getItem("tracking.view") || "kanban");
  const now = useNowTick(30000);

  const setViewPersist = (v) => { setView(v); localStorage.setItem("tracking.view", v); };

  const refresh = useCallback(async () => {
    const [l, s, t, d] = await Promise.all([
      api.get("/loads"),
      api.get("/sites"),
      api.get("/trucks"),
      api.get("/drivers"),
    ]);
    setLoads(l.data); setSites(s.data); setTrucks(t.data); setDrivers(d.data);
  }, []);
  useEffect(() => { refresh(); }, [refresh]);

  const nextStatus = (s) => ({ scheduled: "picked_up", picked_up: "in_transit", in_transit: "delivered" }[s]);

  const advance = async (load) => {
    const next = nextStatus(load.status);
    if (!next) return;
    try {
      await api.patch(`/loads/${load.id}/status`, { status: next });
      toast.success(`${load.bol_number} → ${STATUS_LABEL[next]}`);
      refresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const groupByStatus = (status) => loads.filter((l) => l.status === status);

  return (
    <div>
      <PageHeader
        title="Live Tracking"
        subtitle="Fleet in motion"
        actions={
          <div className="inline-flex border border-gray-300 bg-white" role="tablist">
            <button
              onClick={() => setViewPersist("kanban")}
              data-testid="tracking-view-kanban"
              className={`flex items-center gap-1.5 px-3 py-2 text-[10px] uppercase tracking-widest font-semibold border-r border-gray-300 transition-colors ${
                view === "kanban" ? "bg-gray-900 text-white" : "text-gray-600 hover:bg-gray-50"
              }`}
            >
              <LayoutGrid className="w-3 h-3" /> Kanban
            </button>
            <button
              onClick={() => setViewPersist("map")}
              data-testid="tracking-view-map"
              className={`flex items-center gap-1.5 px-3 py-2 text-[10px] uppercase tracking-widest font-semibold transition-colors ${
                view === "map" ? "bg-gray-900 text-white" : "text-gray-600 hover:bg-gray-50"
              }`}
            >
              <MapIcon className="w-3 h-3" /> Map
            </button>
          </div>
        }
      />
      <div className="p-8">
        {view === "map" ? (
          <TrackingMap loads={loads} sites={sites} trucks={trucks} drivers={drivers} />
        ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {COLUMNS.map((col) => {
            const items = groupByStatus(col.key);
            return (
              <div key={col.key} className="bg-white border border-gray-200 flex flex-col min-h-[400px]" data-testid={`column-${col.key}`}>
                <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
                  <div>
                    <div className="label-caps">{col.label}</div>
                    <div className="font-display font-bold text-2xl tracking-tight text-gray-900">{items.length}</div>
                  </div>
                  <StatusBadge status={col.key} />
                </div>
                <div className="flex-1 p-3 space-y-3 overflow-y-auto">
                  {items.length === 0 ? (
                    <div className="text-xs text-gray-400 font-mono py-6 text-center">—</div>
                  ) : items.map((l) => {
                    const pickup = sites.find((s) => s.id === l.pickup_site_id);
                    const drop = sites.find((s) => s.id === l.dropoff_site_id);
                    const truck = trucks.find((t) => t.id === l.truck_id);
                    const driver = drivers.find((d) => d.id === l.driver_id);
                    const next = nextStatus(l.status);
                    return (
                      <div key={l.id} className="border border-gray-200 p-3 hover:border-gray-900 transition-colors" data-testid={`load-card-${l.bol_number}`}>
                        <div className="flex items-center justify-between mb-2">
                          <div className="font-mono text-[11px] text-gray-900">{l.bol_number}</div>
                          {l.hazardous && (
                            <span className="text-[9px] uppercase tracking-widest font-bold bg-red-50 text-red-800 border border-red-200 px-1.5 py-0.5 flex items-center gap-1">
                              <AlertTriangle className="w-2.5 h-2.5" /> HAZ
                            </span>
                          )}
                        </div>
                        <div className="text-sm font-semibold text-gray-900 mb-1">{l.soil_type}</div>
                        <div className="text-xs text-gray-600 mb-2 font-mono">{l.weight_tons}t · {l.volume_yards}yd³</div>
                        <div className="text-[11px] text-gray-700 flex items-center gap-1 mb-2">
                          <span className="truncate">{pickup?.name || "—"}</span>
                          <ArrowRight className="w-3 h-3 text-gray-400 flex-shrink-0" />
                          <span className="truncate">{drop?.name || "—"}</span>
                        </div>
                        {(() => {
                          const est = travelEstimate(pickup, drop);
                          if (!est) return null;
                          // ETA countdown only for in_transit loads
                          let etaChip = null;
                          if (l.status === "in_transit") {
                            const startMs = inTransitStartMs(l);
                            if (startMs) {
                              const etaMs = startMs + est.oneWay * 60000;
                              const remainingMin = (etaMs - now) / 60000;
                              const overdue = remainingMin < 0;
                              etaChip = (
                                <div
                                  className={`flex items-center gap-1.5 text-[10px] font-mono px-2 py-1 mb-2 border ${
                                    overdue
                                      ? "text-red-800 bg-red-50 border-red-200 animate-pulse"
                                      : remainingMin < 5
                                      ? "text-amber-800 bg-amber-50 border-amber-200"
                                      : "text-blue-800 bg-blue-50 border-blue-200"
                                  }`}
                                  data-testid={`eta-countdown-${l.bol_number}`}
                                >
                                  <Timer className="w-3 h-3" />
                                  <span>
                                    {overdue
                                      ? `Overdue ${fmtMin(remainingMin)} · ETA passed`
                                      : remainingMin < 1
                                      ? `Arriving now`
                                      : `ETA in ${fmtMin(remainingMin)}`}
                                  </span>
                                </div>
                              );
                            }
                          }
                          return (
                            <>
                              <div className="flex items-center gap-1.5 text-[10px] font-mono text-emerald-800 bg-emerald-50 border border-emerald-100 px-2 py-1 mb-2" data-testid={`travel-estimate-${l.bol_number}`}>
                                <Clock className="w-3 h-3" />
                                <span>{est.distance.toFixed(1)}mi · one-way {fmtMin(est.oneWay)} · RT {fmtMin(est.roundTrip)}</span>
                              </div>
                              {etaChip}
                            </>
                          );
                        })()}
                        <div className="flex items-center justify-between border-t border-gray-100 pt-2 mt-2">
                          <div className="text-[10px] font-mono text-gray-500">
                            {truck ? truck.truck_number : "—"} · {driver ? driver.name.split(" ")[0] : "—"}
                          </div>
                          {next && (
                            <button
                              onClick={() => advance(l)}
                              data-testid={`track-advance-${l.bol_number}`}
                              className="text-[10px] uppercase tracking-widest font-semibold bg-gray-900 text-white px-2 py-1 hover:bg-orange-500 transition-colors"
                            >
                              Advance →
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
        )}
      </div>
    </div>
  );
}
